/* eslint-disable @typescript-eslint/no-explicit-any */

import { MeasurementUtils } from "@/utils/MeasurementUtils";
import { useEditor, useNode } from "@craftjs/core";
import { Box, styled } from "@mui/material";
import debounce from "debounce";
import { Resizable } from "re-resizable";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

interface IndicatorsProps {
    bound?: "row" | "column";
}

const Indicators = styled(Box, {
    shouldForwardProp: (prop) => prop !== "bound",
})<IndicatorsProps>(({ bound }) => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    "& span": {
        position: "absolute",
        width: 10,
        height: 10,
        background: "#fff",
        borderRadius: "100%",
        display: "block",
        boxShadow: "0px 0px 12px -1px rgba(0, 0, 0, 0.25);",
        zIndex: 10000,
        pointerEvents: "none",
        border: "2px solid #05B692",
        "&:nth-of-type(1)": {
            ...(bound
                ? bound === "row"
                    ? {
                          left: "50%",
                          top: -5,
                          transform: "translateX(-50%);",
                      }
                    : {
                          left: -5,
                          top: "50%",
                          transform: "translateY(-50%);",
                      }
                : {
                      left: -5,
                      top: -5,
                  }),
        },
        "&:nth-of-type(2)": {
            right: -5,
            top: -5,
            display: bound ? "none" : "block",
        },
        "&:nth-of-type(3)": {
            ...(bound
                ? bound === "row"
                    ? {
                          left: "50%",
                          bottom: -5,
                          transform: "translateX(-50%);",
                      }
                    : {
                          right: -5,
                          bottom: "50%",
                          transform: "translateY(50%);",
                      }
                : {
                      left: -5,
                      bottom: -5,
                  }),
        },
        "&:nth-of-type(4)": {
            right: -5,
            bottom: -5,
            display: bound ? "none" : "block",
        },
    },
}));

interface Dimensions {
    width: any;
    height: any;
}

interface ResizerProps extends React.HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

const Resizer = ({ children, ...props }: ResizerProps) => {
    const {
        id,
        actions: { setProp },
        connectors: { connect },
        fillSpace,
        nodeWidth,
        nodeHeight,
        parent,
        active,
        inNodeContext,
    } = useNode((node) => ({
        parent: node.data.parent,
        active: node.events.selected,
        nodeWidth: node.data.props.width,
        nodeHeight: node.data.props.height,
        fillSpace: node.data.props.fillSpace,
    }));

    const { isRootNode, parentDirection } = useEditor((state, query) => {
        return {
            parentDirection: parent && state.nodes[parent] && state.nodes[parent].data.props.flexDirection,
            isRootNode: query.node(id).isRoot(),
        };
    });

    const resizable = useRef<Resizable>();
    const isResizing = useRef<boolean>(false);
    const editingDimensions = useRef<any>(null);
    const nodeDimensions = useRef<Dimensions | null>();
    nodeDimensions.current = { width: nodeWidth, height: nodeHeight };

    const [internalDimensions, setInternalDimensions] = useState<Dimensions>({ width: nodeWidth, height: nodeHeight });

    const updateInternalDimensionsInPx = useCallback(() => {
        if (!nodeDimensions.current || !resizable.current || !resizable.current.resizable?.parentElement) return;

        const { width: nodeWidth, height: nodeHeight } = nodeDimensions.current;

        const width = MeasurementUtils.percentToPx(
            nodeWidth,
            resizable.current && MeasurementUtils.getElementDimensions(resizable.current.resizable.parentElement).width
        );
        const height = MeasurementUtils.percentToPx(
            nodeHeight,
            resizable.current && MeasurementUtils.getElementDimensions(resizable.current.resizable.parentElement).height
        );

        setInternalDimensions({
            width,
            height,
        });
    }, []);

    const updateInternalDimensionsWithOriginal = useCallback(() => {
        if (!nodeDimensions.current) return;

        const { width: nodeWidth, height: nodeHeight } = nodeDimensions.current;

        setInternalDimensions({
            width: nodeWidth,
            height: nodeHeight,
        });
    }, []);

    const getUpdatedDimensions = (width: any, height: any) => {
        const dom = resizable.current?.resizable;
        if (!dom) return;

        const currentWidth = parseInt(editingDimensions.current.width);
        const currentHeight = parseInt(editingDimensions.current.height);

        return {
            width: currentWidth + parseInt(width),
            height: currentHeight + parseInt(height),
        };
    };

    useEffect(() => {
        if (!isResizing.current) updateInternalDimensionsWithOriginal();
    }, [nodeWidth, nodeHeight, updateInternalDimensionsWithOriginal]);

    useEffect(() => {
        const onResize = debounce(updateInternalDimensionsWithOriginal, 1);
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [updateInternalDimensionsWithOriginal]);

    return (
        <Resizable
            enable={["top", "left", "bottom", "right", "topLeft", "topRight", "bottomLeft", "bottomRight"].reduce(
                (acc: any, key) => {
                    acc[key] = active && inNodeContext;
                    return acc;
                },
                {}
            )}
            ref={(ref) => {
                if (ref) {
                    resizable.current = ref;
                    connect(resizable.current.resizable!);
                }
            }}
            size={internalDimensions}
            maxWidth={"100%"}
            minWidth={props.style?.flex ? nodeWidth : "unset"}
            onResizeStart={(e) => {
                updateInternalDimensionsInPx();
                e.preventDefault();
                e.stopPropagation();
                const dom = resizable.current?.resizable;
                if (!dom || !dom.parentElement) return;
                editingDimensions.current = {
                    width: dom.getBoundingClientRect().width,
                    height: dom.getBoundingClientRect().height,
                };
                isResizing.current = true;
            }}
            onResize={(_, __, ___, d) => {
                const dom = resizable.current?.resizable;
                if (!dom || !dom.parentElement) return;

                let { width, height }: any = getUpdatedDimensions(d.width, d.height);
                if (MeasurementUtils.isPercentage(nodeWidth))
                    width =
                        MeasurementUtils.pxToPercent(
                            width,
                            MeasurementUtils.getElementDimensions(dom.parentElement).width
                        ) + "%";
                else width = `${width}px`;

                if (MeasurementUtils.isPercentage(nodeHeight))
                    height =
                        MeasurementUtils.pxToPercent(
                            height,
                            MeasurementUtils.getElementDimensions(dom.parentElement).height
                        ) + "%";
                else height = `${height}px`;

                if (MeasurementUtils.isPercentage(width) && dom.parentElement.style.width === "auto") {
                    width = editingDimensions.current.width + d.width + "px";
                }

                if (MeasurementUtils.isPercentage(height) && dom.parentElement.style.height === "auto") {
                    height = editingDimensions.current.height + d.height + "px";
                }

                setProp((prop: any) => {
                    prop["width"] = width;
                    prop["height"] = height;
                }, 500);
            }}
            onResizeStop={() => {
                isResizing.current = false;
                updateInternalDimensionsWithOriginal();
            }}
            style={{
                ...(isRootNode ? { margin: "auto" } : {}),
                ...props.style,
            }}
            className={`craft-resizer ${props.className || ""}`}
        >
            {children}
            {active && (
                <Indicators bound={fillSpace === "yes" ? parentDirection : false}>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                </Indicators>
            )}
        </Resizable>
    );
};

export default Resizer;
